import { useLayoutEffect, useState } from "react";

/**
 * How many columns a CSS grid lays out right now, whatever breakpoints or
 * container queries decide them. For drawing something "after the first
 * row": the computed grid-template-columns lists one track per column.
 *
 * Returns a callback ref, so a grid that mounts later is still measured.
 */
export const useGridColumnCount = <T extends HTMLElement = HTMLElement>(): [
  (element: T | null) => void,
  number,
] => {
  const [element, setElement] = useState<T | null>(null);
  const [columns, setColumns] = useState(1);

  useLayoutEffect(() => {
    if (!element) return;
    const measure = () =>
      setColumns(
        getComputedStyle(element).gridTemplateColumns.split(" ").filter(Boolean)
          .length || 1,
      );
    measure();

    if (typeof ResizeObserver === "undefined") return;
    // A frame later: the re-render can resize the grid, which would trip the
    // ResizeObserver loop error if done inside the callback.
    let frame = 0;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [element]);

  return [setElement, columns];
};
